import { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { apiFetch, readJsonResponse } from '../api/client';
import { useTheme } from '../theme/ThemeProvider';

const WINDOW_PRESETS = [
  { days: 30, label: '30 дн.' },
  { days: 90, label: '90 дн.' },
  { days: 180, label: '180 дн.' },
];

const SERIES = [
  { key: 'risk_score', label: 'Risk score', color: '#38bdf8', defaultOn: true },
  { key: 'days_overdue', label: 'Просрочка (дн.)', color: '#fb923c', defaultOn: false },
  { key: 'missed_connection_slots', label: 'Пропуски связи', color: '#f472b6', defaultOn: false },
  { key: 'missed_transmission_windows', label: 'Пропуски передач', color: '#a78bfa', defaultOn: false },
  { key: 'offline_days', label: 'Без связи (дн.)', color: '#94a3b8', defaultOn: false },
];

const SIGNAL_SERIES = [
  { key: 'risk_score', label: 'Risk score', color: '#38bdf8', defaultOn: true },
  { key: 'signal_exposure_percent', label: 'Накопленная тяжесть (%)', color: '#f97316', defaultOn: true },
  { key: 'last_signal_dbm', label: 'Сигнал (дБм)', color: '#22c55e', defaultOn: true },
];

const BATTERY_SERIES = [
  { key: 'risk_score', label: 'Evidence score', color: '#38bdf8', defaultOn: true },
  { key: 'data_quality', label: 'Качество данных', color: '#f97316', defaultOn: true },
  { key: 'risk_quality', label: 'Качество оценки', color: '#a78bfa', defaultOn: false },
  { key: 'last_voltage_mv', label: 'Последний пакет (мВ)', color: '#22c55e', defaultOn: true },
];

function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function shiftDays(isoDate, days) {
  const date = new Date(`${isoDate}T12:00:00`);
  date.setDate(date.getDate() + days);
  return toDateInputValue(date);
}

function buildWindow(days, anchorTo = toDateInputValue(new Date())) {
  return {
    from: shiftDays(anchorTo, -(days - 1)),
    to: anchorTo,
    days,
  };
}

function formatCommentWhen(value) {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatSnapshotDate(value, { withYear = true } = {}) {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString('ru-RU', {
    timeZone: 'Europe/Moscow',
    day: '2-digit',
    month: '2-digit',
    ...(withYear ? { year: 'numeric' } : {}),
  });
}

export default function RiskHistoryModal({
  deviceId,
  serialNumber,
  riskNumber = 1,
  integrationId = null,
  onClose,
}) {
  const { isDark } = useTheme();
  const availableSeries =
    riskNumber === 3 ? SIGNAL_SERIES : riskNumber === 4 ? BATTERY_SERIES : SERIES;
  const [windowDays, setWindowDays] = useState(90);
  const [range, setRange] = useState(() => buildWindow(90));
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [enabledSeries, setEnabledSeries] = useState(() =>
    Object.fromEntries(availableSeries.map((series) => [series.key, series.defaultOn])),
  );

  const chartChrome = useMemo(
    () =>
      isDark
        ? {
            grid: '#1e293b',
            axis: '#64748b',
            tooltipBg: '#020617',
            tooltipBorder: '#334155',
          }
        : {
            grid: '#e2e8f0',
            axis: '#64748b',
            tooltipBg: '#ffffff',
            tooltipBorder: '#cbd5e1',
          },
    [isDark],
  );

  const [isActive, setIsActive] = useState(true);
  const [comments, setComments] = useState([]);
  const [trackingLoading, setTrackingLoading] = useState(true);
  const [trackingSaving, setTrackingSaving] = useState(false);
  const [trackingError, setTrackingError] = useState('');
  const [trackingMessage, setTrackingMessage] = useState('');

  useEffect(() => {
    if (!deviceId) {
      return undefined;
    }

    let cancelled = false;

    async function loadHistory() {
      setLoading(true);
      setError('');

      try {
        const query = new URLSearchParams({
          device_id: String(deviceId),
          from: range.from,
          to: range.to,
        });
        if (riskNumber === 2 && integrationId != null) {
          query.set('integration_id', String(integrationId));
        }
        const response = await apiFetch(`/api/risk/${riskNumber}/history?${query.toString()}`);
        const payload = await readJsonResponse(response, 'История риска');

        if (!response.ok) {
          throw new Error(payload.message || `Ошибка ${response.status}`);
        }

        if (!cancelled) {
          setPoints(Array.isArray(payload.points) ? payload.points : []);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError.message || 'Не удалось загрузить историю');
          setPoints([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [deviceId, range.from, range.to, riskNumber, integrationId]);

  useEffect(() => {
    if (!deviceId) {
      return undefined;
    }

    let cancelled = false;

    async function loadTracking() {
      setTrackingLoading(true);
      setTrackingError('');
      setTrackingMessage('');

      try {
        const response = await apiFetch(`/api/devices/${deviceId}/tracking`);
        const payload = await readJsonResponse(response, 'Статус контроллера');

        if (!response.ok) {
          throw new Error(payload.message || `Ошибка ${response.status}`);
        }

        if (!cancelled) {
          setIsActive(payload.is_active !== false);
          setComments(Array.isArray(payload.comments) ? payload.comments : []);
        }
      } catch (loadError) {
        if (!cancelled) {
          setTrackingError(loadError.message || 'Не удалось загрузить статус');
          setIsActive(true);
          setComments([]);
        }
      } finally {
        if (!cancelled) {
          setTrackingLoading(false);
        }
      }
    }

    loadTracking();
    return () => {
      cancelled = true;
    };
  }, [deviceId]);

  const chartData = useMemo(
    () =>
      points.map((point) => {
        const features = point.assessment_json?.features || point.features || {};
        const riskScore =
          point.risk_score == null || point.risk_score === '' ? null : Number(point.risk_score);
        return {
          ...point,
          risk_score: Number.isFinite(riskScore) ? riskScore : null,
          signal_exposure_percent:
            point.signal_exposure == null ? null : Number(point.signal_exposure) * 100,
          data_quality:
            point.data_quality == null ? features.data_quality ?? null : Number(point.data_quality),
          risk_quality: point.risk_quality == null ? null : Number(point.risk_quality),
          last_voltage_mv:
            features.last_sample_voltage_mv ??
            features.last_voltage_mv ??
            point.last_voltage_mv ??
            null,
          measurement_regime_id: features.measurement_regime_id || null,
          battery_epoch_id: point.battery_epoch_id ?? features.battery_epoch_id ?? null,
          label: formatSnapshotDate(point.snapshot_date, { withYear: false }),
        };
      }),
    [points],
  );

  const timelineEvents = useMemo(() => {
    const events = [];
    let prev = null;

    for (const point of points) {
      const firmware =
        point.extra?.firmware_version ||
        point.extra?.controller_firmware_version ||
        null;
      const prevFirmware =
        prev?.extra?.firmware_version ||
        prev?.extra?.controller_firmware_version ||
        null;

      if (!prev || prev.risk_level !== point.risk_level) {
        events.push({
          date: formatSnapshotDate(point.snapshot_date),
          kind: 'risk_level',
          text: `Уровень риска: ${point.risk_level || '—'} (score ${Number(point.risk_score || 0).toFixed(1)})`,
        });
      }

      if (firmware && firmware !== prevFirmware) {
        events.push({
          date: formatSnapshotDate(point.snapshot_date),
          kind: 'firmware',
          text: `Прошивка: ${firmware}`,
        });
      }

      const regime =
        point.assessment_json?.features?.measurement_regime_id ||
        point.features?.measurement_regime_id ||
        null;
      const prevRegime =
        prev?.assessment_json?.features?.measurement_regime_id ||
        prev?.features?.measurement_regime_id ||
        null;
      if (regime && regime !== prevRegime) {
        events.push({
          date: formatSnapshotDate(point.snapshot_date),
          kind: 'regime',
          text: `Режим измерения: ${regime}`,
        });
      }

      const epochId = point.battery_epoch_id ?? point.assessment_json?.features?.battery_epoch_id;
      const prevEpochId = prev?.battery_epoch_id ?? prev?.assessment_json?.features?.battery_epoch_id;
      if (epochId != null && prev && Number(epochId) !== Number(prevEpochId)) {
        events.push({
          date: formatSnapshotDate(point.snapshot_date),
          kind: 'epoch',
          text: `Новая эпоха батареи: ${epochId}`,
        });
      }

      if (
        point.offline_days != null &&
        (!prev || Number(prev.offline_days) !== Number(point.offline_days)) &&
        Number(point.offline_days) >= 30
      ) {
        events.push({
          date: formatSnapshotDate(point.snapshot_date),
          kind: 'offline',
          text: `Без связи: ${point.offline_days} дн.`,
        });
      }

      prev = point;
    }

    for (const comment of comments) {
      events.push({
        date: formatCommentWhen(comment.created_at),
        kind: 'comment',
        text: comment.created_by
          ? `Комментарий (${comment.created_by}): ${comment.text}`
          : `Комментарий: ${comment.text}`,
      });
    }

    if (!isActive) {
      events.push({
        date: 'сейчас',
        kind: 'archive',
        text: 'Контроллер помечен неактивным',
      });
    }

    return events.slice(-40).reverse();
  }, [points, comments, isActive]);

  function applyPreset(days) {
    setWindowDays(days);
    setRange(buildWindow(days));
  }

  function shiftWindow(direction) {
    const delta = direction * windowDays;
    setRange((current) => ({
      from: shiftDays(current.from, delta),
      to: shiftDays(current.to, delta),
      days: windowDays,
    }));
  }

  function toggleSeries(key) {
    setEnabledSeries((current) => ({
      ...current,
      [key]: !current[key],
    }));
  }

  function applyTrackingPayload(payload, message) {
    setIsActive(payload.is_active !== false);
    setComments(Array.isArray(payload.comments) ? payload.comments : []);
    setTrackingMessage(message);
  }

  async function saveActive(nextActive) {
    setTrackingSaving(true);
    setTrackingError('');
    setTrackingMessage('');

    try {
      const response = await apiFetch(`/api/devices/${deviceId}/tracking`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: nextActive }),
      });
      const payload = await readJsonResponse(response, 'Сохранение статуса');

      if (!response.ok) {
        throw new Error(payload.message || `Ошибка ${response.status}`);
      }

      applyTrackingPayload(payload, 'Статус сохранён');
    } catch (saveError) {
      setTrackingError(saveError.message || 'Не удалось сохранить');
      setIsActive(!nextActive);
    } finally {
      setTrackingSaving(false);
    }
  }

  async function handleActiveChange(checked) {
    setIsActive(checked);
    await saveActive(checked);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 dark:bg-slate-950/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="risk-history-title"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 dark:border-slate-800 px-4 py-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">История Risk #{riskNumber}</p>
            <h2 id="risk-history-title" className="truncate text-lg font-semibold text-slate-900 dark:text-white">
              {serialNumber || `device_id ${deviceId}`}
            </h2>
            <p className="text-xs text-slate-500">
              device_id={deviceId} · {range.from} — {range.to}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 dark:border-slate-700 px-2 py-1 text-sm text-slate-700 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-500"
          >
            Закрыть
          </button>
        </div>

        <div className="space-y-3 border-b border-slate-200 dark:border-slate-800 px-4 py-3">
          <label className="inline-flex items-center gap-2 text-sm text-slate-800 dark:text-slate-200">
            <input
              type="checkbox"
              checked={isActive}
              disabled={trackingLoading || trackingSaving}
              onChange={(event) => handleActiveChange(event.target.checked)}
              className="h-4 w-4 rounded border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900"
            />
            <span>Контроллер активный</span>
            {!isActive && <span className="text-xs text-amber-300/90">(неактивный)</span>}
          </label>
          {(trackingMessage || trackingError) && (
            <div className="flex flex-wrap gap-2 text-xs">
              {trackingMessage && <span className="text-emerald-300">{trackingMessage}</span>}
              {trackingError && <span className="text-rose-300">{trackingError}</span>}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 dark:border-slate-800 px-4 py-3">
          {WINDOW_PRESETS.map((preset) => (
            <button
              key={preset.days}
              type="button"
              onClick={() => applyPreset(preset.days)}
              className={`rounded-lg border px-2.5 py-1 text-xs font-medium ${
                windowDays === preset.days
                  ? 'border-sky-400/50 bg-sky-400/10 text-sky-700 dark:text-sky-200'
                  : 'border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500'
              }`}
            >
              {preset.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => shiftWindow(-1)}
            className="rounded-lg border border-slate-300 dark:border-slate-700 px-2.5 py-1 text-xs text-slate-700 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-500"
          >
            ← раньше
          </button>
          <button
            type="button"
            onClick={() => shiftWindow(1)}
            className="rounded-lg border border-slate-300 dark:border-slate-700 px-2.5 py-1 text-xs text-slate-700 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-500"
          >
            позже →
          </button>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-slate-200 dark:border-slate-800 px-4 py-2">
          {availableSeries.map((series) => (
            <label
              key={series.key}
              className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-200 dark:border-slate-800 px-2 py-1 text-xs text-slate-700 dark:text-slate-300"
            >
              <input
                type="checkbox"
                checked={Boolean(enabledSeries[series.key])}
                onChange={() => toggleSeries(series.key)}
              />
              <span style={{ color: series.color }}>{series.label}</span>
            </label>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
          {loading && <p className="text-sm text-slate-500 dark:text-slate-400">Загружаем историю…</p>}
          {!loading && error && <p className="text-sm text-rose-300">{error}</p>}
          {!loading && !error && points.length === 0 && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Снимков пока нет. История пишется для всех контроллеров (включая score=0) после утреннего
                прогона (06:00 МСК).
              </p>
              {timelineEvents.length > 0 && (
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 p-3">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Хронология</p>
                  <ul className="space-y-1.5">
                    {timelineEvents.map((event, index) => (
                      <li
                        key={`${event.date}-${event.kind}-${index}`}
                        className="flex gap-2 text-xs text-slate-700 dark:text-slate-300"
                      >
                        <span className="w-28 shrink-0 tabular-nums text-slate-500">{event.date}</span>
                        <span>{event.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {!loading && !error && points.length > 0 && (
            <>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke={chartChrome.grid} strokeDasharray="3 3" />
                    <XAxis dataKey="label" stroke={chartChrome.axis} tick={{ fontSize: 11 }} />
                    <YAxis stroke={chartChrome.axis} tick={{ fontSize: 11 }} width={40} />
                    <Tooltip
                      contentStyle={{
                        background: chartChrome.tooltipBg,
                        border: `1px solid ${chartChrome.tooltipBorder}`,
                        borderRadius: 8,
                        color: isDark ? '#e2e8f0' : '#0f172a',
                      }}
                      labelFormatter={(_, payload) => formatSnapshotDate(payload?.[0]?.payload?.snapshot_date)}
                    />
                    <Legend />
                    {availableSeries.filter((series) => enabledSeries[series.key]).map((series) => (
                      <Line
                        key={series.key}
                        type="monotone"
                        dataKey={series.key}
                        name={series.label}
                        stroke={series.color}
                        dot={false}
                        strokeWidth={2}
                        connectNulls={riskNumber !== 4}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-4 rounded-xl border border-slate-200 dark:border-slate-800 p-3">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Хронология</p>
                {timelineEvents.length === 0 ? (
                  <p className="text-xs text-slate-500">Пока нет событий уровня/прошивки/комментария.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {timelineEvents.map((event, index) => (
                      <li
                        key={`${event.date}-${event.kind}-${index}`}
                        className="flex gap-2 text-xs text-slate-700 dark:text-slate-300"
                      >
                        <span className="w-28 shrink-0 tabular-nums text-slate-500">{event.date}</span>
                        <span>{event.text}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                <table className="min-w-full text-left text-xs text-slate-700 dark:text-slate-300">
                  <thead className="bg-slate-100 dark:bg-slate-900/80 text-slate-500">
                    <tr>
                      <th className="px-3 py-2 font-medium">Дата</th>
                      <th className="px-3 py-2 font-medium">Score</th>
                      <th className="px-3 py-2 font-medium">Уровень</th>
                      {riskNumber === 3 ? (
                        <>
                          <th className="px-3 py-2 font-medium">Тяжесть</th>
                          <th className="px-3 py-2 font-medium">Сигнал</th>
                          <th className="px-3 py-2 font-medium">Замеров</th>
                        </>
                      ) : riskNumber === 4 ? (
                        <>
                          <th className="px-3 py-2 font-medium">Данные</th>
                          <th className="px-3 py-2 font-medium">Оценка</th>
                          <th className="px-3 py-2 font-medium">Режим</th>
                          <th className="px-3 py-2 font-medium">мВ</th>
                        </>
                      ) : (
                        <>
                          <th className="px-3 py-2 font-medium">Просрочка</th>
                          <th className="px-3 py-2 font-medium">Пропуски связи</th>
                          <th className="px-3 py-2 font-medium">Пропуски передач</th>
                          <th className="px-3 py-2 font-medium">Offline</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {[...points].reverse().map((point) => (
                      <tr key={`${point.snapshot_date}-${point.battery_epoch_id || 0}-${point.model_version || ''}`} className="border-t border-slate-200 dark:border-slate-800">
                        <td className="px-3 py-1.5">{formatSnapshotDate(point.snapshot_date)}</td>
                        <td className="px-3 py-1.5 tabular-nums">
                          {point.risk_score == null || Number.isNaN(Number(point.risk_score))
                            ? '—'
                            : Number(point.risk_score).toFixed(1)}
                        </td>
                        <td className="px-3 py-1.5">{point.risk_level}</td>
                        {riskNumber === 3 ? (
                          <>
                            <td className="px-3 py-1.5 tabular-nums">
                              {point.signal_exposure != null ? `${(Number(point.signal_exposure) * 100).toFixed(1)}%` : '—'}
                            </td>
                            <td className="px-3 py-1.5 tabular-nums">
                              {point.last_signal_dbm != null ? `${point.last_signal_dbm} дБм` : '—'}
                            </td>
                            <td className="px-3 py-1.5 tabular-nums">{point.sample_count ?? '—'}</td>
                          </>
                        ) : riskNumber === 4 ? (
                          <>
                            <td className="px-3 py-1.5 tabular-nums">
                              {point.data_quality == null ? '—' : Number(point.data_quality).toFixed(0)}
                            </td>
                            <td className="px-3 py-1.5 tabular-nums">
                              {point.risk_quality == null ? '—' : Number(point.risk_quality).toFixed(0)}
                            </td>
                            <td className="px-3 py-1.5">
                              {point.assessment_json?.features?.measurement_regime_id || '—'}
                            </td>
                            <td className="px-3 py-1.5 tabular-nums">
                              {point.assessment_json?.features?.last_sample_voltage_mv ?? '—'}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-3 py-1.5 tabular-nums">{point.days_overdue ?? '—'}</td>
                            <td className="px-3 py-1.5 tabular-nums">{point.missed_connection_slots ?? '—'}</td>
                            <td className="px-3 py-1.5 tabular-nums">{point.missed_transmission_windows ?? '—'}</td>
                            <td className="px-3 py-1.5 tabular-nums">{point.offline_days ?? '—'}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
